import { Form, Input } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { TagInput } from "@/pages/GovernancePage/components/organisms/ScmForm/components/TagInput"
import { BaseReposLabel } from "@/pages/IntegrationsPage/components/molecules/BaseReposLabel"
import {
  isGuid,
  isValidAcrLoginServer,
  isValidAcrRepository,
  normalizeAcrLoginServer,
  normalizeAcrRepository
} from "@/pages/IntegrationsPage/helpers/acr"

import styles from "./FormAcr.module.scss"

import type { AcrFormValues } from "@/pages/IntegrationsPage/hooks/useAcrAPI"
import type { FormAcrProps } from "."

const GUID_PLACEHOLDER = "00000000-0000-0000-0000-000000000000"
const PREFIX = "integrations.acr.form"

const guidRule = (message: string) => ({
  validator: (_: unknown, value?: string) =>
    value && isGuid(value)
      ? Promise.resolve()
      : Promise.reject(new Error(message))
})

export const FormAcr = (props: FormAcrProps) => {
  const { form, formId, registry, onFieldsChange, onFinish, t } = props
  const { t: tConnectAcr } = useTranslation("translation", {
    keyPrefix: "integrations.connect.acr"
  })
  const isNew = !registry
  const initialValues: AcrFormValues = registry
    ? {
        azureTenantId: registry.azureTenantId || "",
        clientId: registry.clientId || "",
        repositories: registry.repositories
      }
    : { azureTenantId: "", clientId: "", registry: "", repositories: [] }

  return (
    <Form
      className={styles.form}
      form={form}
      id={formId}
      layout="vertical"
      initialValues={initialValues}
      onFieldsChange={onFieldsChange}
      onFinish={onFinish}
    >
      <Form.Item
        label={t(`${PREFIX}.azureTenantId.label`)}
        name="azureTenantId"
        required={false}
        rules={[guidRule(t(`${PREFIX}.azureTenantId.invalid`))]}
      >
        <Input
          ref={props.focusRef}
          autoComplete="off"
          placeholder={GUID_PLACEHOLDER}
        />
      </Form.Item>
      <Form.Item
        label={t(`${PREFIX}.clientId.label`)}
        tooltip={t(`${PREFIX}.clientId.tooltip`)}
        name="clientId"
        required={false}
        rules={[guidRule(t(`${PREFIX}.clientId.invalid`))]}
      >
        <Input autoComplete="off" placeholder={GUID_PLACEHOLDER} />
      </Form.Item>
      {isNew && (
        <Form.Item
          label={t(`${PREFIX}.registry.label`)}
          name="registry"
          required={false}
          normalize={(value: string) => value?.trim()}
          rules={[
            {
              validator: (_: unknown, value?: string) =>
                value && isValidAcrLoginServer(normalizeAcrLoginServer(value))
                  ? Promise.resolve()
                  : Promise.reject(new Error(t(`${PREFIX}.registry.invalid`)))
            }
          ]}
        >
          <Input
            autoComplete="off"
            placeholder={t(`${PREFIX}.registry.placeholder`)}
          />
        </Form.Item>
      )}
      <Form.Item
        name="repositories"
        label={<BaseReposLabel t={tConnectAcr} />}
        normalize={(value: string[]) =>
          Array.from(new Set((value || []).map(normalizeAcrRepository))).filter(
            Boolean
          )
        }
        rules={[
          {
            validator: (_: unknown, value: string[] = []) => {
              const invalid = value.find((repo) => !isValidAcrRepository(repo))

              if (!invalid) return Promise.resolve()

              return Promise.reject(
                new Error(
                  t(`${PREFIX}.repositories.invalid`, { entry: invalid })
                )
              )
            }
          }
        ]}
      >
        <TagInput placeholder={tConnectAcr("baseReposInput.placeholder")} />
      </Form.Item>
    </Form>
  )
}
